import { Suspense, useEffect, useLayoutEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { CartRecoveryGuard } from '@/features/cart/CartRecoveryGuard';
import { useSharedCustomerLocation } from '@/features/location/useCustomerLocation';
import { CustomerBottomNavigation } from '@/features/storefront/CustomerBottomNavigation';
import { CustomerHeader } from '@/features/storefront/CustomerHeader';
import { CustomerIcon } from '@/features/storefront/CustomerIcon';
import { StoreSelectorSheet } from '@/features/storefront/StoreSelectorSheet';
import { useAutomaticBrowseStore } from '@/features/storefront/automaticBrowseStore';
import { browseStoreDistance } from '@/features/storefront/storeDistance';
import { t } from '@/i18n/messages';
import { useBrowseStore } from '@/state/browseStoreStore';
import { useCustomerSessionStore } from '@/state/customerSessionStore';
import { validateBranding, type StoreBrandingInput } from '@/theme/branding';

export function CustomerAppLayout() {
  useAutomaticBrowseStore();
  const location = useLocation();

  useEffect(() => {
    if (!('scrollRestoration' in window.history)) return undefined;
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  useLayoutEffect(() => {
    const hash = location.hash ? decodeURIComponent(location.hash.slice(1)) : '';
    if (hash) {
      const frame = window.requestAnimationFrame(() => {
        document.getElementById(hash)?.scrollIntoView({ block: 'start' });
      });
      return () => window.cancelAnimationFrame(frame);
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    return undefined;
  }, [location.hash, location.key, location.pathname, location.search]);

  const customerLocation = useSharedCustomerLocation();
  const [selectorOpen, setSelectorOpen] = useState(false);
  const browseStore = useBrowseStore((state) => state.store);
  const distance = browseStoreDistance(
    browseStore,
    customerLocation.status === 'granted' ? customerLocation.location : undefined
  );
  const store = useCustomerSessionStore((state) => state.store) as
    | (ReturnType<typeof useCustomerSessionStore.getState>['store'] & {
        branding?: StoreBrandingInput;
      })
    | null;
  const branding = validateBranding(
    store?.branding,
    (import.meta.env.VITE_PUBLIC_BRANDING_ASSET_ORIGINS ?? '')
      .split(',')
      .map((value: string) => value.trim())
      .filter(Boolean)
  );
  const isStorefront =
    location.pathname === '/' ||
    location.pathname === '/stores' ||
    location.pathname === '/menu' ||
    location.pathname === '/about' ||
    location.pathname === '/franchise' ||
    location.pathname.startsWith('/events/') ||
    location.pathname.startsWith('/moods') ||
    /\/store\/[^/]+\/menus(?:\/[^/]+)?$/.test(location.pathname) ||
    /\/store\/[^/]+\/menu$/.test(location.pathname);

  return (
    <div
      className={`customer-app-shell ${isStorefront ? 'is-storefront' : 'is-transactional'} brand-card-${branding.cardStyle}`}
      style={branding.style}
    >
      <CartRecoveryGuard />
      {!isStorefront && branding.bannerUrl && (
        <img
          className="store-brand-banner"
          src={branding.bannerUrl}
          width="720"
          height="240"
          alt=""
          decoding="async"
        />
      )}
      <CustomerHeader branding={branding} storeCode={browseStore?.storeCode} />
      <div className="route-content">
        <Suspense
          fallback={
            <div className="route-loading" role="status" aria-live="polite">
              {t('app.loading')}
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </div>
      <button
        type="button"
        className="selected-store-bar"
        onClick={() => setSelectorOpen(true)}
        aria-label={browseStore?.name ?? 'Select Store'}
      >
        <CustomerIcon name="pin" />
        <span>
          {browseStore ? (
            <>
              <strong>{browseStore.name}</strong>
              {browseStore.address && <small>{browseStore.address}</small>}
              {distance && <small>{distance} (ước tính)</small>}
              {browseStore.selectionSource !== 'manual' && <small>Tự động chọn cửa hàng</small>}
            </>
          ) : (
            <strong>Select Store</strong>
          )}
        </span>
        <CustomerIcon name="chevron" />
      </button>
      <CustomerBottomNavigation storeCode={browseStore?.storeCode} />
      <StoreSelectorSheet open={selectorOpen} onClose={() => setSelectorOpen(false)} />
      <footer className="customer-footer">{t('app.footer')}</footer>
    </div>
  );
}
